import { Box } from "@chakra-ui/react";
import { customEvent, getCdnImageSrc } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  TAG_FILTER_OPTIONS,
  getFilteredContents,
  AllContentCard,
} from "../constants/contentData";

const PAGE_SIZE = 8;

export default function ContentAllSection() {
  const [tagKey, setTagKey] = useState("전체");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLabel =
    TAG_FILTER_OPTIONS.find((o) => o.key === tagKey)?.label ?? "컨텐츠 전체";
  const filtered = getFilteredContents(tagKey);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (key: string) => {
    customEvent({
      newGtm: {
        path: "content",
        trigger_and_target: `filter_tag_${key}`,
      },
    });
    setTagKey(key);
    setVisibleCount(PAGE_SIZE);
    setIsOpen(false);
  };

  const handleLoadMore = () => {
    customEvent({
      newGtm: {
        path: "content",
        trigger_and_target: "btn_more_content",
      },
    });
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  return (
    <BaseStack spacing={0} pt={24} pb={32}>
      {/* 필터 드롭다운 */}
      <Box px="20px" pb="16px">
        <Box ref={dropdownRef} position="relative">
          {/* 트리거 버튼 */}
          <Box
            as="button"
            width="100%"
            onClick={() => setIsOpen((prev) => !prev)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              background: "#f4f5f6",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
            }}
          >
            <Box
              style={{
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: "24px",
                letterSpacing: "-0.1px",
                color: "#171819",
              }}
            >
              {currentLabel}
            </Box>
            {/* AccordionIcon과 동일한 SVG 셰브론 */}
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
              style={{
                color: "#9ea3a8",
                flexShrink: 0,
                transform: isOpen ? "rotate(-180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z" />
            </svg>
          </Box>

          {/* 드롭다운 리스트 */}
          {isOpen && (
            <Box
              position="absolute"
              top="calc(100% + 4px)"
              left={0}
              right={0}
              bg="#ffffff"
              border="1px solid #e8e9ea"
              borderRadius="6px"
              boxShadow="0px 4px 16px rgba(0,0,0,0.08)"
              zIndex={100}
              overflow="hidden"
            >
              {TAG_FILTER_OPTIONS.map((option, index) => (
                <Box
                  key={option.key}
                  borderBottom={
                    index < TAG_FILTER_OPTIONS.length - 1
                      ? "1px solid #f4f5f6"
                      : "none"
                  }
                >
                  <Box
                    as="button"
                    width="100%"
                    onClick={() => handleSelect(option.key)}
                    style={{
                      display: "block",
                      padding: "14px 16px",
                      textAlign: "left",
                      background: tagKey === option.key ? "#f4f5f6" : "#ffffff",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: tagKey === option.key ? 600 : 400,
                      lineHeight: "22px",
                      letterSpacing: "-0.3px",
                      color: tagKey === option.key ? "#171819" : "#46494c",
                      width: "100%",
                    }}
                  >
                    {option.label}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* 카드 리스트 */}
      <BaseStack spacing={24} px={20}>
        {visible.map((card) => (
          <ContentCard key={card.id} card={card} />
        ))}
      </BaseStack>

      {/* 더 보기 버튼 */}
      {hasMore && (
        <Box px="20px" pt="32px">
          <Box
            as="button"
            width="100%"
            onClick={handleLoadMore}
            style={{
              padding: "12px 24px",
              border: "1px solid #dddedf",
              borderRadius: "6px",
              background: "#ffffff",
              color: "#46494c",
              fontSize: "15px",
              fontWeight: 500,
              lineHeight: "22px",
              letterSpacing: "-0.3px",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            더 보기
          </Box>
        </Box>
      )}
    </BaseStack>
  );
}

function ContentCard({ card }: { card: AllContentCard }) {
  return (
    <Box
      as="a"
      href={card.href}
      target={card.href.startsWith("http") ? "_blank" : undefined}
      rel={card.href.startsWith("http") ? "noopener noreferrer" : undefined}
      style={{ display: "block", width: "100%", textDecoration: "none" }}
      onClick={() => {
        customEvent({
          newGtm: {
            path: "content",
            trigger_and_target: `uv_feed-${card.id}`,
          },
        });
      }}
    >
      <BaseStack spacing={12}>
        <Box
          position="relative"
          width="100%"
          borderRadius="12px"
          overflow="hidden"
          background={card.thumbnailBg}
          style={{ aspectRatio: "16/9" }}
        >
          {card.thumbnailSrc && (
            <Image
              src={getCdnImageSrc(card.thumbnailSrc)}
              alt={card.title}
              fill
              style={{ objectFit: "cover" }}
            />
          )}
        </Box>
        <BaseStack spacing={6}>
          <Box
            style={{
              color: "#171819",
              fontSize: "21px",
              fontWeight: 600,
              lineHeight: "28px",
              letterSpacing: "-0.2px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {card.title}
          </Box>
          <Box
            style={{
              color: "#46494c",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
              letterSpacing: "-0.1px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {card.description}
          </Box>
          <Box style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {card.tags.map((tag) => (
              <Box
                key={tag}
                style={{
                  background: "#ffffff",
                  border: "1px solid #dddedf",
                  borderRadius: "4px",
                  padding: "4px 6px",
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#46494c",
                  lineHeight: "16px",
                  letterSpacing: "-0.1px",
                }}
              >
                {tag}
              </Box>
            ))}
          </Box>
        </BaseStack>
      </BaseStack>
    </Box>
  );
}
